import { IApiRequestOptions } from '@import-io/doc-sdk';
import { IHistoryItem } from '_/common/history/history.types';
import { IComment } from '_/common/types/comments.types';
import { IReadme, IReadmeVersion } from '_/common/types/readme.types';
import { IFlow } from '_/features/flows/common/types/flow.types';
import { IFlowSegment } from '_/features/flows/common/types/flowSegments.types';
import { ISaveFlowDTO } from '_/features/flows/flowPage/flowPage.types';
import { IListResponse } from '_/types/common.types';
import { IDelivery } from '_/types/deliveries/delivery.types';
import zeusSdk from '_/utils/SDKs';

export const requestFlowList = async (projectId: string, params = {}): Promise<IFlow[]> => {
  const { items } = await zeusSdk.projects.getChildren(projectId, 'flows', params);
  return items as IFlow[];
};

export const requestFlow = async (projectId: string, flowSlug: string, options = {}): Promise<IFlow> => {
  return (await zeusSdk.projects.getChildById(projectId, 'flows', flowSlug, options)) as IFlow;
};

export const requestFlowSegments = async (flowId: string, params = {}): Promise<IFlowSegment[]> => {
  const { items } = await zeusSdk.flows.getChildren(flowId, 'segments', params);
  return items as IFlowSegment[];
};

export const fetchFlowSegmentByCollection = async (flowId: string, collectionId: string): Promise<IFlowSegment | null> => {
  try {
    if (!flowId || !collectionId) {
      return null;
    }

    const { items: allSegments } = await zeusSdk.flows.getChildren(flowId, 'segments', {
      include: 'settings',
    });

    return (allSegments as IFlowSegment[]).find((segment) => segment.collectionId === collectionId) || null;
  } catch (error) {
    console.error('Error fetching flow segment', error);
    return null;
  }
};

export const createFlowRequest = async (projectId: string, body: Partial<ISaveFlowDTO>): Promise<IFlow> => {
  return zeusSdk.projects.createChild(projectId, 'flows', body);
};

export const updateFlowRequest = async (flowId: string, flow: Record<string, unknown>): Promise<IFlow> => {
  return zeusSdk.flows.patch(flowId, flow);
};

export const addFlowReadmeRequest = async (flowId: string, content: string): Promise<IReadme> => {
  return (await zeusSdk.flows.addReadme(flowId, { content: content })) as IReadme;
};

export const updateFlowReadmeRequest = async (flowId: string, content: string): Promise<IReadme> => {
  return zeusSdk.flows.updateChild(flowId, 'readme', { content: content });
};

export const runFlowRequest = async (flowId: string, flowOverride: Record<string, unknown> = {}): Promise<IDelivery> => {
  return zeusSdk.flows.start(flowId, {}, flowOverride);
};

export const getFlowReadmeVersionsRequest = async (id: string, params: IApiRequestOptions = {}): Promise<IReadmeVersion[]> => {
  const options = {
    ...params,
    include: 'editor',
    queryParameters: {
      sortDirection: 'DESC', // Latest versions first
      ...params.queryParameters,
    },
  };
  const { items } = await zeusSdk.flows.getReadmeVersions(id, options);
  return items as IReadmeVersion[];
};

export const getFlowCommentsRequest = async (id: string, params: IApiRequestOptions = {}): Promise<IComment[]> => {
  const { items } = await zeusSdk.flows.listComments(id, params);
  return items as IComment[];
};

export const saveFlowCommentRequest = async (id: string, comment: string): Promise<IComment> => {
  return (await zeusSdk.flows.postComment(id, comment)) as IComment;
};

export const getFlowHistoryRequest = async (
  id: string,
  params: IApiRequestOptions = {},
): Promise<IListResponse<IHistoryItem<IFlow>>> => {
  return (await zeusSdk.flows.getChild(id, '_versions', params)) as IListResponse<IHistoryItem<IFlow>>;
};

export const getFlowDeliveriesRequest = async (
  flowId: string,
  filter?: string,
  cursor?: string | null,
): Promise<{ items: IDelivery[]; cursor: string | null }> => {
  try {
    const options: any = {
      queryParameters: {
        limit: 50,
        sort: 'addedAt',
        sortDirection: 'DESC',
        filter: filter || undefined,
      },
      include: 'flow.project',
    };

    if (cursor) {
      options.queryParameters.cursor = cursor;
    }

    const response = await zeusSdk.flows.getChildren(flowId, 'deliveries', options);
    return {
      items: response?.items || [],
      cursor: response?.metadata?.cursor || null,
    };
  } catch (error) {
    console.error('Error fetching flow deliveries', error);
    return { items: [], cursor: null };
  }
};
